import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useData } from '@/state/data';
import { formatDateTime, formatOsloSpan, hoursBetween } from '@/domain/time';
import { machineLabel, presentText } from '@/lib/present';
import { fmtNumberFlex } from '@/domain/numbers';
import { QUALITY_LABELS } from '@/lib/labels';
import { SourceStatusBadge } from './Badges';
import { cn } from '@/lib/utils';

const RecordContext = createContext(null);

/** Finner en kildepost (produksjon, linjetilstand eller maskinintervall) etter id eller sourceRecordId. */
export function findRecord(dataset, id) {
  if (!dataset || !id) return null;
  const p = dataset.production.find((x) => x.id === id || x.sourceRecordId === id);
  if (p) {
    return {
      kind: 'production',
      kindLabel: 'Produksjonsperiode',
      id: p.id,
      sourceId: p.sourceId,
      sourceRecordId: p.sourceRecordId,
      revision: p.revision,
      fromUtc: p.fromUtc,
      toUtc: p.toUtc,
      quality: p.quality,
      fields: [{ field: 'producedTonnes', label: 'Produsert mengde', value: p.producedTonnes, unit: 't' }],
      extra: [{ label: 'Fraksjon', value: dataset.fractions.find((f) => f.fractionId === p.fractionId)?.name ?? p.fractionId }, { label: 'Linje', value: p.lineId }],
      raw: p,
    };
  }
  const s = dataset.lineStates.find((x) => x.id === id || x.sourceRecordId === id);
  if (s) {
    return {
      kind: 'lineState',
      kindLabel: 'Linjetilstand',
      id: s.id,
      sourceId: s.sourceId,
      sourceRecordId: s.sourceRecordId,
      revision: s.revision,
      fromUtc: s.fromUtc,
      toUtc: s.toUtc,
      quality: 'measured',
      fields: [{ field: 'duration', label: 'Varighet', value: hoursBetween(s.fromUtc, s.toUtc), unit: 'h' }],
      extra: [{ label: 'Tilstand', value: s.state === 'production' ? 'Produksjon' : s.state === 'stop' ? 'Stopp' : 'Planlagt driftsfri' }, ...(s.stopReason ? [{ label: 'Stoppårsak', value: s.stopReason }] : [])],
      raw: s,
    };
  }
  const m = dataset.machineIntervals.find((x) => x.id === id || x.sourceRecordId === id);
  if (m) {
    return {
      kind: 'machine',
      kindLabel: 'Maskinintervall',
      id: m.id,
      sourceId: m.sourceId,
      sourceRecordId: m.sourceRecordId,
      revision: m.revision,
      fromUtc: m.fromUtc,
      toUtc: m.toUtc,
      quality: m.quality,
      ingestedAtUtc: m.ingestedAtUtc,
      fields: [
        { field: 'engineHours', label: 'Motortimer', value: m.engineHours, unit: 'h' },
        { field: 'idleHours', label: 'Tomgangstimer', value: m.idleHours, unit: 'h' },
        { field: 'fuelLitres', label: 'Dieselforbruk', value: m.fuelLitres, unit: 'L' },
        { field: 'idleFuelLitres', label: 'Målt tomgangsdrivstoff', value: m.idleFuelLitres, unit: 'L', optional: true },
      ],
      extra: [{ label: 'Maskin', value: `${machineLabel(m.machineId)} · ${dataset.machines.find((x) => x.machineId === m.machineId)?.fictionalName ?? ''}`.replace(/ · $/, ''), link: `/maskiner/${m.machineId}` }],
      raw: m,
    };
  }
  const l = dataset.contextLoads.find((x) => x.id === id);
  if (l) {
    return {
      kind: 'context',
      kindLabel: 'Kontekst (SmartDok)',
      id: l.id,
      sourceId: l.sourceId,
      sourceRecordId: l.id,
      revision: 1,
      fromUtc: l.fromUtc,
      toUtc: l.toUtc,
      quality: 'measured',
      fields: [{ field: 'loadedTonnes', label: 'Lastet mengde', value: l.tonnes, unit: 't' }],
      extra: [{ label: 'Destinasjon', value: l.destination }],
      raw: l,
    };
  }
  return null;
}

export function RecordViewerProvider({ children }) {
  const [recordId, setRecordId] = useState(null);
  const openRecord = useCallback((id) => setRecordId(id), []);
  const value = useMemo(() => ({ openRecord, recordId, close: () => setRecordId(null) }), [openRecord, recordId]);
  return (
    <RecordContext.Provider value={value}>
      {children}
      <SourceRecordDialog recordId={recordId} onClose={() => setRecordId(null)} />
    </RecordContext.Provider>
  );
}

export function useRecordViewer() {
  const ctx = useContext(RecordContext);
  if (!ctx) throw new Error('useRecordViewer utenfor RecordViewerProvider');
  return ctx;
}

export function SourceRecordDialog({ recordId, onClose }) {
  const { dataset, sources } = useData();
  const rec = findRecord(dataset, recordId);
  const source = rec ? sources.find((s) => s.sourceId === rec.sourceId) : null;
  return (
    <Dialog open={!!recordId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="source-record-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Kildepost <span className="font-mono text-base font-medium">{presentText(rec ? rec.sourceRecordId : recordId)}</span></DialogTitle>
          <DialogDescription>{rec ? `${rec.kindLabel} · ${source?.name ?? rec.sourceId}` : 'Fant ikke kildeposten i gjeldende datasett.'}</DialogDescription>
        </DialogHeader>
        {rec && (
          <div className="space-y-4 text-sm">
            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5">
              <dt className="text-[var(--hr-muted)]">Tidsrom (Oslo)</dt>
              <dd className="hr-tabular">{formatOsloSpan(rec.fromUtc, rec.toUtc)}</dd>
              <dt className="text-[var(--hr-muted)]">Tidsrom (UTC)</dt>
              <dd className="hr-tabular text-[13px]">[{rec.fromUtc}, {rec.toUtc})</dd>
              <dt className="text-[var(--hr-muted)]">Intern ID</dt>
              <dd className="font-mono text-[13px]">{presentText(rec.id)}</dd>
              <dt className="text-[var(--hr-muted)]">Revisjon</dt>
              <dd className="hr-tabular">{rec.revision}</dd>
              <dt className="text-[var(--hr-muted)]">Kvalitet</dt>
              <dd>{QUALITY_LABELS[rec.quality] ?? rec.quality}</dd>
              {rec.ingestedAtUtc && (
                <>
                  <dt className="text-[var(--hr-muted)]">Innlest</dt>
                  <dd className="hr-tabular">{formatDateTime(rec.ingestedAtUtc)}</dd>
                </>
              )}
              {rec.extra.map((e) => (
                <React.Fragment key={e.label}>
                  <dt className="text-[var(--hr-muted)]">{e.label}</dt>
                  <dd>{e.value}</dd>
                </React.Fragment>
              ))}
            </dl>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--hr-border)] text-left text-[13px] text-[var(--hr-muted)]">
                  <th className="py-1.5 font-medium">Felt</th>
                  <th className="py-1.5 text-right font-medium">Råverdi</th>
                  <th className="py-1.5 text-right font-medium">Enhet</th>
                </tr>
              </thead>
              <tbody>
                {rec.fields.map((f) => (
                  <tr key={f.field} className="border-b border-[var(--hr-border)] last:border-0">
                    <td className="py-1.5">{f.label}</td>
                    <td className={cn('py-1.5 text-right hr-tabular', f.value === null && !f.optional && 'text-[var(--hr-amber)] font-medium')}>
                      {f.value === null ? (f.optional ? 'ikke målt' : 'null (missing)') : fmtNumberFlex(f.value, 4)}
                    </td>
                    <td className="py-1.5 text-right text-[var(--hr-muted)]">{f.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {source && (
              <div className="flex items-center gap-2 text-[13px] text-[var(--hr-muted)]">
                Kildestatus: <SourceStatusBadge status={source.status} /> <span>{source.modeLabel}</span>
              </div>
            )}
            <p className="text-[13px] text-[var(--hr-muted)]">Råverdi og transformasjonsversjon ({presentText(dataset?.seedVersion)}) beholdes. Beregninger lenker tilbake til denne posten via intern ID.</p>
          </div>
        )}
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="source-record-close-button">
            Lukk
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Liste av kildeposter som kan åpnes. */
export function ProvenanceList({ recordIds = [], limit = 8, label = 'Kildeposter', testId = 'provenance-list' }) {
  const [showAll, setShowAll] = useState(false);
  const { openRecord } = useRecordViewer();
  const ids = showAll ? recordIds : recordIds.slice(0, limit);
  if (!recordIds.length) return <p className="text-sm text-[var(--hr-muted)]">Ingen kildeposter.</p>;
  return (
    <div data-testid={testId}>
      <p className="mb-1.5 text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">
        {label} ({recordIds.length})
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {ids.map((id) => (
          <li key={id}>
            <button
              type="button"
              onClick={() => openRecord(id)}
              data-testid={`provenance-record-${id}`}
              className="rounded-md border border-[var(--hr-border)] bg-white px-2 py-0.5 font-mono text-[13px] text-[var(--hr-text)] transition-colors duration-150 hover:border-[var(--hr-accent)] hover:text-[var(--hr-accent-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]"
            >
              {presentText(id)}
            </button>
          </li>
        ))}
        {recordIds.length > limit && (
          <li>
            <button type="button" onClick={() => setShowAll((s) => !s)} className="hr-link px-2 py-0.5 text-[13px]" data-testid={`${testId}-toggle`}>
              {showAll ? 'Vis færre' : `Vis alle ${recordIds.length}`}
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}
